import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/Gallery.css';

const Gallery: React.FC = () => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // State for modal visibility

  // Array of gallery images with their paths relative to the public directory
  const images = [
    { 
      id: 1, 
      // British English comment: Changed the first image to 'fishing4.jpg' for variety
      src: process.env.PUBLIC_URL + '/images/fishing111.jpg', 
      alt: 'Fishing at Loch Lomond' 
    },
    { 
      id: 2, 
      src: process.env.PUBLIC_URL + '/images/fishingboat2.jpg', 
      alt: 'Beautiful fishing boat' 
    },
    { 
      id: 3, 
      src: process.env.PUBLIC_URL + '/images/fishingboat3.jpg', 
      alt: 'Catch of the day' 
    },
    { 
      id: 4, 
      src: process.env.PUBLIC_URL + '/images/fishing3.jpg', 
      alt: 'Fishing equipment' 
    },
    { 
      id: 5, 
      src: process.env.PUBLIC_URL + '/images/fishing5.jpg', 
      alt: 'Sunset fishing' 
    },
    { 
      id: 6, 
      src: process.env.PUBLIC_URL + '/images/fishing7.jpg', 
      alt: 'Fishing boat' 
    }
  ];

  // Function to handle image loading errors
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error(`Failed to load image: ${e.currentTarget.src}`);
    e.currentTarget.src = process.env.PUBLIC_URL + '/images/hero-bg.jpg'; // Fallback image
  };

  return (
    <section id="gallery" className="gallery-section">
      <div className="gallery-container">
        <h2>{t('gallery.title')}</h2>
        <div className="gallery-grid">
          {images.map((image) => (
            <div
              key={image.id}
              className="gallery-item"
              onClick={() => setSelectedImage(image.src)}
            >
              <img 
                src={image.src} 
                alt={image.alt} // British English comment: Descriptive alt text for SEO
                onError={handleImageError}
              />
            </div>
          ))}
        </div>
        {/* View More Photos button opens the modal */}
        <button className="view-more-btn" 
          onClick={() => setShowModal(true)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { setShowModal(true); } }}
          aria-pressed="false"
          tabIndex={0}
          onTouchStart={e => e.currentTarget.classList.add('pressed')}
          onTouchEnd={e => e.currentTarget.classList.remove('pressed')}
          onMouseDown={e => e.currentTarget.classList.add('pressed')}
          onMouseUp={e => e.currentTarget.classList.remove('pressed')}
          onBlur={e => e.currentTarget.classList.remove('pressed')}
          // British English comment: Ensures button is accessible and gives visual feedback on press
        >
          {t('gallery.viewMore')}
        </button>
      </div>

      {/* Single image modal (when a gallery image is clicked) */}
      {selectedImage && (
        <div className="modal" onClick={() => setSelectedImage(null)}>
          <div className="modal-content">
            <button 
              className="modal-close-btn" 
              onClick={() => setSelectedImage(null)}
              aria-label="Close image"
            >
              &times;
            </button>
            <button 
              className="modal-back-btn"
              onClick={() => setSelectedImage(null)}
              aria-label="Go back"
            >
              Go Back
            </button>
            <img 
              src={selectedImage} 
              alt="Selected gallery - Anchor Management Loch Lomond"
              onError={handleImageError}
            />
          </div>
        </div>
      )}

      {/* Gallery grid modal */}
      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button 
              className="modal-close-btn" 
              onClick={() => setShowModal(false)}
              aria-label="Close gallery"
            >
              &times;
            </button>
            <button 
              className="modal-back-btn"
              onClick={() => setShowModal(false)}
              aria-label="Go back"
            >
              Go Back
            </button>
            <div className="modal-gallery-grid">
              {images.map((image) => (
                <div key={image.id} className="modal-gallery-img-wrapper">
                  <img
                    src={image.src}
                    alt={image.alt}
                    onError={handleImageError}
                    className="modal-gallery-img"
                  />
                </div>
              ))}
            </div>
            <button 
              className="modal-exit-btn"
              onClick={() => setShowModal(false)}
              aria-label="Exit gallery"
            >
              Exit Gallery
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery; 